import type { FC } from "react";
import {
  Award01,
  Bell01,
  ClockRewind,
  Coins01,
  Key01,
  LayoutAlt01,
  Link01,
  Lock01,
  Palette,
  Server01,
  Settings01,
  Tool01,
  Users01,
} from "@untitledui/icons";

/**
 * Jediný zdroj pravdy pro strukturu sekce Nastavení — horní tab lišta (skupiny),
 * pill řádek (položky skupiny) i rozcestník na `/settings` čtou tenhle config.
 *
 * Modul je záměrně bez Reactu a bez závislosti na license store: gating se
 * předává jako predikát `show(featureId)`, takže je celý testovatelný.
 */

export type SettingsGroupId = "overview" | "system" | "operations" | "access" | "appearance";

/**
 * Stav položky vůči licenci. Vidět jsou všechny tři — `advertised`/`locked`
 * se ale označují odznakem, aby bylo předem jasné, že za nimi obsah nebude.
 */
export type SettingsItemState = "enabled" | "advertised" | "locked";

export interface SettingsItem {
  /** Absolutní cesta v SPA. */
  to: string;
  /** i18n klíč popisku (sdílený s pill řádkem i kartou rozcestníku). */
  labelKey: string;
  /** i18n klíč jednořádkového popisu — recykluje `*.subtitle` klíč dané stránky. */
  descKey: string;
  icon: FC<{ className?: string }>;
  /** Feature ID; `undefined` = položka je vždy viditelná. */
  feature?: string;
  /** Doplní `visibleGroups`, pokud dostane druhý argument. */
  state?: SettingsItemState;
}

export interface SettingsGroup {
  id: SettingsGroupId;
  labelKey: string;
  /** Cíl tabu — první viditelná položka skupiny (u `overview` samotné `/settings`). */
  to: string;
  /** Prázdné u `overview`: nemá pill řádek, místo toho renderuje rozcestník. */
  items: SettingsItem[];
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  { id: "overview", labelKey: "settings.group.overview", to: "/settings", items: [] },
  {
    id: "system",
    labelKey: "settings.group.system",
    to: "/settings/general",
    items: [
      { to: "/settings/general", labelKey: "settings.general", descKey: "general.subtitle", icon: Settings01 },
      { to: "/settings/token-settings", labelKey: "settings.tokenSettings", descKey: "tokenSettings.subtitle", icon: Coins01 },
      { to: "/settings/tls", labelKey: "settings.tls", descKey: "tls.subtitle", icon: Lock01, feature: "admin.tls" },
      { to: "/settings/license", labelKey: "settings.license", descKey: "license.subtitle", icon: Award01 },
    ],
  },
  {
    id: "operations",
    labelKey: "settings.group.operations",
    to: "/settings/connectors",
    items: [
      {
        to: "/settings/connectors",
        labelKey: "settings.connectors",
        descKey: "connectors.subtitle",
        icon: Link01,
        feature: "admin.connector-config",
      },
      {
        to: "/settings/maintenance",
        labelKey: "settings.maintenance",
        descKey: "maintenance.subtitle",
        icon: Tool01,
        feature: "admin.maintenance.cleanup",
      },
      {
        to: "/settings/notifications",
        labelKey: "settings.notifications",
        descKey: "notificationSettings.subtitle",
        icon: Bell01,
        feature: "admin.notifications",
      },
    ],
  },
  {
    id: "access",
    labelKey: "settings.group.access",
    to: "/settings/users",
    items: [
      { to: "/settings/users", labelKey: "settings.users", descKey: "users.subtitle", icon: Users01, feature: "admin.user-management" },
      { to: "/settings/api-keys", labelKey: "settings.apiKeys", descKey: "apikeys.subtitle", icon: Key01, feature: "admin.api-keys" },
      { to: "/settings/directory", labelKey: "settings.directory", descKey: "directory.subtitle", icon: Server01, feature: "admin.ldap-auth" },
      { to: "/settings/audit", labelKey: "settings.audit", descKey: "audit.subtitle", icon: ClockRewind, feature: "admin.user-management" },
    ],
  },
  {
    id: "appearance",
    labelKey: "settings.group.appearance",
    to: "/settings/appearance",
    items: [
      { to: "/settings/appearance", labelKey: "settings.appearance", descKey: "appearance.subtitle", icon: Palette },
      { to: "/settings/ui-settings", labelKey: "settings.uiSettings", descKey: "uiSettings.subtitle", icon: LayoutAlt01 },
    ],
  },
];

function matches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + "/");
}

/**
 * Skupina odpovídající dané cestě. `/settings` (přesně) → `overview`,
 * pod-cesty položek (detail routes) → vlastnící skupina, neznámá cesta → `null`.
 */
export function activeGroupId(pathname: string): SettingsGroupId | null {
  const path = pathname.replace(/\/+$/, "") || "/settings";
  if (path === "/settings") return "overview";
  for (const group of SETTINGS_GROUPS) {
    if (group.items.some((item) => matches(path, item.to))) return group.id;
  }
  return null;
}

/**
 * Skupiny viditelné pro aktuální licenci: položky bez viditelné feature vypadnou,
 * skupina bez zbylých položek se skryje a tab skupiny míří na první **použitelnou**
 * položku, aby navigace nikdy neskončila na zamčené routě. `overview` zůstává vždy.
 *
 * Volitelný `stateOf` doplní `state` na položky (pro odznaky „Brzy" / „Zamčeno")
 * a zpřesní cíl tabu; bez něj se modul chová jako dřív.
 */
export function visibleGroups(
  show: (feature: string) => boolean,
  stateOf?: (feature: string) => SettingsItemState,
): SettingsGroup[] {
  const withState = (item: SettingsItem): SettingsItem =>
    stateOf && item.feature ? { ...item, state: stateOf(item.feature) } : item;

  return SETTINGS_GROUPS.flatMap((group) => {
    if (group.id === "overview") return [group];
    const items = group.items.filter((item) => !item.feature || show(item.feature)).map(withState);
    if (items.length === 0) return [];
    const target = items.find((item) => (item.state ?? "enabled") === "enabled") ?? items[0];
    return [{ ...group, to: target.to, items }];
  });
}
